import { ValueOf } from '@ahha/utils/@types/typeUtils'

import { Paginated } from '@/api/Base/types'
import { LabelingNodeInfo } from '@/api/LabelingNode/types'
import { PROJECT_NODE_TYPE } from '@/api/Project/types'
import { BaseNodeInfo, NodeItem } from '@/api/Node/types'

import { ModelValueType, TabularRequestStatusType } from '@/pages/const'
import { TabularValueType } from '@/api/Tabular/types'

export type CommonListPayload = {
  keyword?: string,
  page?: number,
  itemsPerPage?: number,
  sortKey?: string,
  sortDir?: string,
}

type DatasetNodeBase = {
  numOfDatasetItems: number,
  nodeType: 'DATASET'
  isAuto: boolean
  labelingNodes?: LabelingNodeInfo[]
  date?: Date
  category?: DatasetCategoryType
  failFileCount?: number
  failList?: { fileName: string, error: string }[]
  referenceInferenceNodeId: string | null
  tabularSummaryId?: string
  tabularRequestStatus?: TabularRequestStatusType
  videoExtractionStatus?: TabularRequestStatusType
  readOnly?: boolean
  isDisabled?: boolean
  isUsedAsDriftReference?: boolean
  numOfFilteredItems?: number
  isCreatedCollectedData?: boolean
}

export type DatasetNodeInfo = BaseNodeInfo<DatasetNodeBase>

export type FileMeta = {
  md5: string,
  fileSize: number,
  uploader: string,
  status: string // FIXME: change type
  referenceCount: number,
  mimeType: string,
  mediaInfo: {
    width: number,
    height: number,
    videoCodec: string,
  }
  createdAt: Date,
  uploadedAt: Date,
}

type DatasetNodeItemBase = { nodeType: typeof PROJECT_NODE_TYPE.DATASET, date: string }
export type DatasetNodeItem = NodeItem<DatasetNodeItemBase>

export type CreateDatasetNodePayload = {
  projectId: string
  name?: string
  desc?: string
  metadata?: any
  position: { x: number, y: number }
  isOpsNode?: boolean
}

export type UploadFilesToDatasetPayload = {
  datasetNodeId: string
  files: File[]
}

export type UploadFilesToDatasetResponse = {
  datasetNode: DatasetNodeInfo
  failFileCount: number
  failList: { fileName: string, error: string }[]
}

export type ImportStorageItemsToDatasetPayload = {
  datasetNodeId: string
  storage_item_ids: string[]
}

export type ImportTabularStorageItemsToDatasetPayload = {
  datasetNodeId: string
  storage_item_ids: string[]
  tabularType: TabularValueType
  delimiter: string
  useHeader: boolean
  startRow: number
}

export type AnnotationFormatType = 'YOLO' | 'PASCAL_VOC' | 'CVAT' | 'LABEL_ME' | 'DAISY'

export type ImportLabelingPayload = {
  projectId: string
  file: File
  modelType: ModelValueType
  datasetNodeName?: string
  labelingNodeName?: string
  datasetNodeMetadata?: any
  labelingNodeMetadata?: any
  datasetNodePosition: { x: number, y: number }
  labelingNodePosition: { x: number, y: number }
  formatType?: AnnotationFormatType // TODO: 추후 서버 수정이후, 기존 cvat, labelme도 보내야함
}

export type ImportLabelingResponse = {
  datasetNode: DatasetNodeInfo
  labelingNode: LabelingNodeInfo
}

export type DeleteDatasetNodeItemPayload = {
  projectId: string,
  datasetNodeId: string,
  datasetItemId: string,
}

export type DeleteItemsPayload = {
  datasetNodeId: string
  datasetItemIds: string[]
}

export interface EditDatasetNodePayload {
  id: string,
  projectId: string,
  name?: string,
  date?: string,
  metadata?: any
}

export type UploadRawFilePayload = {
  datasetNodeId: string
  file: File
  width: string
  height: string
  extension: string
  pixelType: string
  byteOrder: string
}

export type UploadRawFileResponse = {
  datasetNodeId: string
  numOfDatasetItems: number
}

export type UploadTabularFilePayload = {
  datasetNodeId: string
  files: File[]
  tabularType: string // 'TABULAR' | 'TIME_SERIES'
  delimiter: string
  useHeader: boolean
  startRow: string
}

export type AddCollectedItemsPayload = {
  datasetNodeId: string
  dataCollectionInfoId: string
  imageKey: string
  itemIds: string[]
}

export type AddCollectedFilteredItemsPayload = {
  datasetNodeId: string
  dataCollectionInfoId: string
  imageKey?: string
  from: string
  to: string
  datasetCategory: DatasetCategoryType
  filters?: any[]
  tabularType?: TabularValueType
}

export type DatasetNodeItemListResponse = Paginated<DatasetNodeItem[]>

export const DATASET_CATEGORY = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
  TABULAR: 'TABULAR',
}
export type DatasetCategoryType = ValueOf<typeof DATASET_CATEGORY>

export type UploadVideoFilesPayload = {
  datasetNodeId: string
  files: File[]
  fps: string
}

export type UploadVideoFilesResponse = {
  datasetNode: DatasetNodeInfo
  failFileCount: number
  failList: { fileName: string, error: string }[]
}

export type UploadActualVideoFilesPayload = {
  datasetNodeId: string
  files: File[]
}

export type UploadActualVideoFilesResponse = {
  datasetNode: DatasetNodeInfo
  failFileCount: number
  failList: { fileName: string, error: string }[]
}

export type GetTabularDatasetItemPayload = {
  projectId: string
  datasetNodeId: string
  datasetItemId: string
  enabled?: boolean
} & CommonListPayload
export type GetTabularDatasetItemResponse = Paginated<{ [key: string]: any }[]>

export interface AddModelVersionItemsPayload {
  datasetNodeId: string
  versionId: string
}
